import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, NavLink } from 'react-router-dom';
import UnlimitedFlasher from '../flasher/UnlimitedFlasher';
import UserManagement from '../UserManagement';
import UserDetail from '../UserDetail';
import FirmwareManagement from '../FirmwareManagement';
import DeviceManagement from '../DeviceManagement';
import DeviceDetailPage from '../DeviceDetailPage';
import DeviceDashboard from '../analytics/DeviceDashboard';
import InternalDeployView from './InternalDeployView';
import BetaProgramView from './BetaProgramView';
import DeployTestPage from './DeployTestPage';
import DeployTestResultsPage from './DeployTestResultsPage';
import { INTERNAL_PROGRAM } from '../../config/deployProgram';
import ProductionDeployView from './ProductionDeployView';
import SurveysView from './SurveysView';
import SurveyEditorPage from './SurveyEditorPage';
import SurveyResultsPage from './SurveyResultsPage';
import SurveyQuestionResponsesPage from './SurveyQuestionResponsesPage';
import SurveyResultPage from './SurveyResultPage';
import ServerLogsView from '../ServerLogsView';
import ServerChangelogView from './ServerChangelogView';
import SettingsView from './SettingsView';
import { User } from "../../types";
import { API_BASE_URL } from '../../config/api';
import { publicUrl } from '../../config/hosts';
import UnreadBadge from '../UnreadBadge';
import { TESTER_UNREAD_EVENT } from '../../utils/tester';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBolt, 
  faCode, 
  faUsers, 
  faMicrochip, 
  faMobileScreen,
  faServer,
  faRightFromBracket,
  faArrowUpRightFromSquare,
  faDownload,
  faChartLine,
  faScrewdriverWrench,
  faFlask,
  faGlobe,
  faBars,
  faXmark,
  faGear,
  faClipboardList,
  faUser,
} from '@fortawesome/free-solid-svg-icons';

interface AdminViewProps {
  user: User;
  onLogout: () => void;
}

interface VersionInfo {
  serverVersion: string;
}

const sidebarLinkClass = ({ isActive }: { isActive: boolean }) =>
  `admin-sidebar-link${isActive ? ' active' : ''}`;

const sidebarSubLinkClass = ({ isActive }: { isActive: boolean }) =>
  `admin-sidebar-link admin-sidebar-sublink${isActive ? ' active' : ''}`;

const canAccessFirmware = (role: User['role']) => ['admin', 'developer'].includes(role);
const canAccessDeploy = (role: User['role']) => ['admin', 'developer'].includes(role);
const canAccessSettings = (role: User['role']) => ['admin', 'developer'].includes(role);
const canAccessSurveys = (role: User['role']) => ['admin', 'developer'].includes(role);

const AdminView: React.FC<AdminViewProps> = ({ user, onLogout }) => {
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [betaUnreadTotal, setBetaUnreadTotal] = useState(0);
  const [internalUnreadTotal, setInternalUnreadTotal] = useState(0);
  const apiBaseUrl = API_BASE_URL;

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  useEffect(() => {
    const fetchVersionInfo = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/version`);
        if (response.ok) {
          setVersionInfo(await response.json());
        }
      } catch (error) {
        console.error('Failed to fetch version info:', error);
      }
    };

    fetchVersionInfo();
  }, [apiBaseUrl]);

  useEffect(() => {
    if (!canAccessDeploy(user.role)) {
      return;
    }

    let cancelled = false;
    const loadUnread = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/chat/unread/testers`, {
          withCredentials: true,
        });
        if (!cancelled) {
          setBetaUnreadTotal(Number(response.data.beta?.total || 0));
          setInternalUnreadTotal(Number(response.data.internal?.total || 0));
        }
      } catch (_err) {
        if (!cancelled) {
          setBetaUnreadTotal(0);
          setInternalUnreadTotal(0);
        }
      }
    };

    loadUnread();
    const timer = window.setInterval(loadUnread, 30000);
    const onUnreadChanged = () => {
      loadUnread();
    };
    window.addEventListener(TESTER_UNREAD_EVENT, onUnreadChanged);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener(TESTER_UNREAD_EVENT, onUnreadChanged);
    };
  }, [apiBaseUrl, user.role]);

  return (
    <div className={`admin-layout${isMobileMenuOpen ? ' mobile-menu-open' : ''}`}>
      <div className="admin-mobile-topbar">
        <button
          type="button"
          className="admin-mobile-menu-button"
          onClick={() => setIsMobileMenuOpen(true)}
          aria-label="Open navigation menu"
          aria-expanded={isMobileMenuOpen}
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
        <div className="admin-mobile-title">
          <img src="/logo-mark.png" width="32" alt="Fluora" />
          <span>Firmware</span>
        </div>
      </div>

      {isMobileMenuOpen && (
        <button
          type="button"
          className="admin-mobile-menu-backdrop"
          onClick={closeMobileMenu}
          aria-label="Close navigation menu"
        />
      )}

      <aside className={`admin-sidebar${isMobileMenuOpen ? ' open' : ''}`}>
        <div className="admin-sidebar-brand">
          <img src="/logo-mark.png" width="48" alt="Fluora" />
          <div className="admin-sidebar-title">Firmware</div>
          <button
            type="button"
            className="admin-sidebar-close"
            onClick={closeMobileMenu}
            aria-label="Close navigation menu"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="admin-sidebar-user">
          <span className="admin-sidebar-user-label">Logged in as</span>{' '}
          <span className="admin-sidebar-user-name">{user.firstName}</span>
        </div>

        <nav className="admin-sidebar-nav">
          <NavLink to="/dashboard" className={sidebarLinkClass} onClick={closeMobileMenu}>
            <FontAwesomeIcon icon={faChartLine} fixedWidth />
            Dashboard
          </NavLink>
          <NavLink to="/users" className={sidebarLinkClass} onClick={closeMobileMenu}>
            <FontAwesomeIcon icon={faUsers} fixedWidth />
            Users
          </NavLink>
          <NavLink to="/devices" className={sidebarLinkClass} onClick={closeMobileMenu}>
            <FontAwesomeIcon icon={faMicrochip} fixedWidth />
            Devices
          </NavLink>
          {canAccessFirmware(user.role) && (
            <NavLink to="/firmware" className={sidebarLinkClass} onClick={closeMobileMenu}>
              <FontAwesomeIcon icon={faCode} fixedWidth />
              Firmware
            </NavLink>
          )}
          {canAccessSurveys(user.role) && (
            <NavLink to="/surveys" className={sidebarLinkClass} onClick={closeMobileMenu}>
              <FontAwesomeIcon icon={faClipboardList} fixedWidth />
              Surveys
            </NavLink>
          )}
          <NavLink to="/flash" className={sidebarLinkClass} onClick={closeMobileMenu}>
            <FontAwesomeIcon icon={faBolt} fixedWidth />
            Flash
          </NavLink>
          <NavLink to="/profile" className={sidebarLinkClass} onClick={closeMobileMenu}>
            <FontAwesomeIcon icon={faUser} fixedWidth />
            Profile
          </NavLink>
          {canAccessSettings(user.role) && (
            <NavLink to="/settings" className={sidebarLinkClass} onClick={closeMobileMenu}>
              <FontAwesomeIcon icon={faGear} fixedWidth />
              Settings
            </NavLink>
          )}

          {canAccessDeploy(user.role) && (
            <>
              <div className="admin-sidebar-section">Testing</div>
              <NavLink to="/deploy/internal" className={sidebarSubLinkClass} onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faScrewdriverWrench} fixedWidth />
                <span className="flex-grow-1">Internal</span>
                <UnreadBadge count={internalUnreadTotal} className="unread-badge-nav" />
              </NavLink>
              <NavLink to="/deploy/beta" className={sidebarSubLinkClass} onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faFlask} fixedWidth />
                <span className="flex-grow-1">Beta</span>
                <UnreadBadge count={betaUnreadTotal} className="unread-badge-nav" />
              </NavLink>
              <div className="admin-sidebar-section">Deploy</div>
              <NavLink to="/deploy/production" className={sidebarSubLinkClass} onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faGlobe} fixedWidth />
                Production
              </NavLink>
            </>
          )}

          <div className="admin-sidebar-section">Logs</div>
          <NavLink to="/logs/server" className={sidebarSubLinkClass} onClick={closeMobileMenu}>
            <FontAwesomeIcon icon={faServer} fixedWidth />
            Server Logs
          </NavLink>

          <div className="admin-sidebar-section">Public Links</div>
          <a href="https://wiki.fluoraplant.com/" target="_blank" rel="noopener noreferrer" className="admin-sidebar-link admin-sidebar-sublink admin-sidebar-external-link" onClick={closeMobileMenu}>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} fixedWidth />
            Wiki
          </a>
          <a href={publicUrl('/releases')} target="_blank" rel="noopener noreferrer" className="admin-sidebar-link admin-sidebar-sublink admin-sidebar-external-link" onClick={closeMobileMenu}>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} fixedWidth />
            Releases
          </a>
          <a href={publicUrl('/mobile-app')} target="_blank" rel="noopener noreferrer" className="admin-sidebar-link admin-sidebar-sublink admin-sidebar-external-link" onClick={closeMobileMenu}>
            <FontAwesomeIcon icon={faMobileScreen} fixedWidth />
            Mobile App
          </a>
          <a href={publicUrl('/download')} target="_blank" rel="noopener noreferrer" className="admin-sidebar-link admin-sidebar-sublink admin-sidebar-external-link" onClick={closeMobileMenu}>
            <FontAwesomeIcon icon={faDownload} fixedWidth />
            Firmware Download
          </a>
          <a href={publicUrl('/legacy')} target="_blank" rel="noopener noreferrer" className="admin-sidebar-link admin-sidebar-sublink admin-sidebar-external-link" onClick={closeMobileMenu}>
            <FontAwesomeIcon icon={faDownload} fixedWidth />
            Legacy Download
          </a>
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-sidebar-version">
            <span className="admin-sidebar-version-label">Server:</span>{' '}
            <span className="admin-sidebar-version-number">
              {versionInfo ? `v${versionInfo.serverVersion}` : '...'}
            </span>
            <NavLink to="/changelog" className="admin-sidebar-version-link" onClick={closeMobileMenu}>
              changelog
            </NavLink>
          </div>

          <button type="button" className="btn btn-sm admin-sidebar-logout-btn" onClick={onLogout}>
            <FontAwesomeIcon icon={faRightFromBracket} className="me-2" />
            Logout
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <div className="admin-main-inner">
          <div className="admin-main-content">
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Navigate to="/dashboard/firmware" replace />} />
              <Route
                path="/dashboard/:tab"
                element={(
                  <DeviceDashboard
                    apiBaseUrl={apiBaseUrl}
                    showSurveyTab={canAccessSurveys(user.role)}
                  />
                )}
              />
              <Route path="/devices/*" element={<DeviceManagement user={user} />} />
              <Route path="/device/:serial" element={<DeviceDetailPage user={user} />} />
              <Route path="/users" element={<UserManagement user={user} />} />
              <Route path="/users/:user_id" element={<UserDetail currentUser={user} />} />
              <Route path="/profile" element={<UserDetail currentUser={user} profileMode />} />
              {canAccessFirmware(user.role) && <Route path="/firmware" element={<FirmwareManagement />} />}
              {canAccessDeploy(user.role) && (
                <Route path="/deploy" element={<Navigate to="/deploy/internal" replace />} />
              )}
              {canAccessDeploy(user.role) && (
                <Route path="/deploy/internal/tests/:testId/results/:reportId" element={<DeployTestResultsPage program={INTERNAL_PROGRAM} />} />
              )}
              {canAccessDeploy(user.role) && (
                <Route path="/deploy/internal/tests/:testId/results" element={<DeployTestResultsPage program={INTERNAL_PROGRAM} />} />
              )}
              {canAccessDeploy(user.role) && (
                <Route path="/deploy/internal/tests/:testId" element={<DeployTestPage program={INTERNAL_PROGRAM} />} />
              )}
              {canAccessDeploy(user.role) && (
                <Route path="/deploy/internal/*" element={<InternalDeployView />} />
              )}
              {canAccessDeploy(user.role) && (
                <Route path="/deploy/beta/tests/:testId/results/:reportId" element={<DeployTestResultsPage />} />
              )}
              {canAccessDeploy(user.role) && (
                <Route path="/deploy/beta/tests/:testId/results" element={<DeployTestResultsPage />} />
              )}
              {canAccessDeploy(user.role) && (
                <Route path="/deploy/beta/tests/:testId" element={<DeployTestPage />} />
              )}
              {canAccessDeploy(user.role) && (
                <Route path="/deploy/beta/*" element={<BetaProgramView />} />
              )}
              {canAccessDeploy(user.role) && (
                <Route path="/deploy/production" element={<ProductionDeployView />} />
              )}
              {canAccessSurveys(user.role) && (
                <Route path="/surveys/new" element={<SurveyEditorPage />} />
              )}
              {canAccessSurveys(user.role) && (
                <Route path="/surveys/dashboard" element={<Navigate to="/dashboard/survey" replace />} />
              )}
              {canAccessSurveys(user.role) && (
                <Route path="/surveys/:surveyId/results/questions/:questionId" element={<SurveyQuestionResponsesPage />} />
              )}
              {canAccessSurveys(user.role) && (
                <Route path="/surveys/:surveyId/results/:resultId" element={<SurveyResultPage />} />
              )}
              {canAccessSurveys(user.role) && (
                <Route path="/surveys/:surveyId/results" element={<SurveyResultsPage />} />
              )}
              {canAccessSurveys(user.role) && (
                <Route path="/surveys/:surveyId" element={<SurveyEditorPage />} />
              )}
              {canAccessSurveys(user.role) && (
                <Route path="/surveys" element={<SurveysView />} />
              )}
              <Route path="/flash" element={<UnlimitedFlasher user={user} />} />
              {canAccessSettings(user.role) && <Route path="/settings" element={<SettingsView />} />}
              <Route path="/changelog" element={<ServerChangelogView />} />
              <Route path="/logs/server" element={<ServerLogsView />} />
              <Route path="/devices/dashboard" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminView;
